/**
 * End-of-level screen: stars pop in, the score counts up, and the stats read
 * back what the climb actually looked like.
 *
 * Never uses failure language (CLAUDE.md). Timed mode is deferred, so stars come
 * from accuracy; "best previous" and unlock news arrive with SaveManager in
 * Prompt 7 and read from the registry the moment they exist.
 */
import Phaser from 'phaser';
import { setDevState } from '../systems/devQuery';
import { drawVerticalGradient } from '../art/backdrop';
import { ensurePlaceholderTexture } from '../art/placeholders';
import { MAX_LEVEL } from '../systems/LevelGenerator';
import { encouragementFor } from '../systems/Scoring';
import { animalById } from '../data/animals';
import { audioOf } from '../systems/audioRegistry';
import { pressButton, styleButton } from './ui/menuButton';
import { SaveProgressPanel } from './ui/SaveProgressPanel';
import { MissesPanel } from './ui/MissesPanel';
import { hasMisses } from '../systems/missReview';
import type { SaveManager } from '../systems/SaveManager';
import { FONT_FAMILY, GAME_HEIGHT, GAME_WIDTH, IS_PORTRAIT, PALETTE } from '../types';
import type { GameRunStats } from '../types';

const STAR_SIZE = 96;
const STAR_GAP = 124;

/** Stat rows sit between the score and the footer line. */
const STAT_ROW_TOP = 344;
const STAT_ROW_SPACING = 34;

interface MenuItem {
  readonly label: string;
  readonly action: () => void;
}

export class Results extends Phaser.Scene {
  private stats: GameRunStats | null = null;
  private readonly labels: Phaser.GameObjects.Text[] = [];
  private items: MenuItem[] = [];
  private selected = 0;
  /** An open overlay (save progress, or what was missed), which has the keyboard. */
  private panel: { close(): void } | null = null;
  /** "What I missed", beside the accuracy row, when there is anything to show. */
  private reviewButton: Phaser.GameObjects.Text | null = null;

  constructor() {
    super('Results');
  }

  init(data: Partial<GameRunStats>): void {
    this.stats = isRunStats(data) ? data : null;
  }

  create(): void {
    setDevState({ scene: 'Results' });
    this.resetSceneState();
    this.cameras.main.setBackgroundColor(PALETTE.sky);
    this.drawBackdrop();

    const stats = this.stats;
    const reached = stats?.reachedGoal === true;

    this.add
      .text(GAME_WIDTH / 2, 74, reached ? 'You made it!' : 'Nice climbing!', {
        fontFamily: FONT_FAMILY,
        fontSize: '62px',
        color: PALETTE.snow,
        stroke: PALETTE.ink,
        strokeThickness: 9,
      })
      .setOrigin(0.5);

    if (stats === null) {
      this.add
        .text(GAME_WIDTH / 2, GAME_HEIGHT / 2, 'No stats were passed to this scene.', {
          fontFamily: FONT_FAMILY,
          fontSize: '26px',
          color: PALETTE.ice,
        })
        .setOrigin(0.5);
      this.buildMenu(1);
      return;
    }

    this.showStars(stats.stars);
    this.countScoreUp(stats.score);
    this.showStatRows(stats);
    this.showFooterMessage(stats);
    this.buildMenu(stats.level);
  }

  /**
   * Phaser reuses the scene instance, so field initialisers only ever run once.
   * Anything that accumulates has to be cleared here or the second visit works
   * on objects destroyed during the first - which crashed on finishing level 2.
   */
  private resetSceneState(): void {
    this.labels.length = 0;
    this.items = [];
    this.selected = 0;
    this.panel = null;
    this.reviewButton = null;
  }

  /* ----------------------------------------------------------------- stars */

  private showStars(earned: number): void {
    ensurePlaceholderTexture(this, 'ui/star_full');
    ensurePlaceholderTexture(this, 'ui/star_empty');

    const left = GAME_WIDTH / 2 - STAR_GAP;
    for (let i = 0; i < 3; i += 1) {
      const filled = i < earned;
      const star = this.add
        .image(left + i * STAR_GAP, 190, filled ? 'ui/star_full' : 'ui/star_empty')
        .setDisplaySize(STAR_SIZE, STAR_SIZE)
        .setAlpha(filled ? 0 : 0.35);

      if (!filled) continue;

      // Pop in one at a time, so three stars feel like three events.
      const delay = 220 + i * 260;
      this.tweens.add({
        targets: star,
        alpha: 1,
        scale: { from: star.scale * 0.3, to: star.scale },
        angle: { from: -35, to: 0 },
        delay,
        duration: 420,
        ease: 'Back.easeOut',
      });
      // One chime per star, on the beat of its own pop rather than all at once.
      this.time.delayedCall(delay, () => audioOf(this)?.play('star'));
    }
  }

  /* ----------------------------------------------------------------- score */

  private countScoreUp(target: number): void {
    const label = this.add
      .text(GAME_WIDTH / 2, 288, 'Score 0', {
        fontFamily: FONT_FAMILY,
        fontSize: '46px',
        color: PALETTE.gold,
        stroke: PALETTE.ink,
        strokeThickness: 7,
      })
      .setOrigin(0.5);

    const counter = { value: 0 };
    this.tweens.add({
      targets: counter,
      value: target,
      duration: Math.min(1400, 400 + target * 2),
      delay: 300,
      ease: 'Cubic.easeOut',
      onUpdate: () => label.setText(`Score ${Math.round(counter.value)}`),
      onComplete: () => label.setText(`Score ${target}`),
    });
  }

  /* ----------------------------------------------------------------- stats */

  private showStatRows(stats: GameRunStats): void {
    const rows: Array<readonly [string, string]> = [
      ['Words', `${stats.wordsCorrect} of ${stats.wordsAttempted}`],
      ['Accuracy', `${Math.round(stats.accuracy)}%`],
      ['Speed', `${Math.round(stats.wpm)} wpm`],
      ['Longest streak', `${stats.longestStreak}`],
      ['Power-ups', this.powerUpSummary(stats)],
      ['Time', `${(stats.elapsedMs / 1000).toFixed(1)}s`],
    ];

    // SaveManager writes the previous best here before starting this scene.
    const best = this.registry.get(`best.level${stats.level}.score`);
    if (typeof best === 'number' && best > 0) {
      rows.push(['Best before', `${best}`]);
    }

    const left = GAME_WIDTH / 2 - 250;
    const right = GAME_WIDTH / 2 + 250;
    rows.forEach(([name, value], index) => {
      const y = STAT_ROW_TOP + index * STAT_ROW_SPACING;
      const label = this.add
        .text(left, y, name, { fontFamily: FONT_FAMILY, fontSize: '25px', color: PALETTE.ice })
        .setOrigin(0, 0.5);
      if (name === 'Accuracy' && hasMisses(stats.wordSlips)) {
        this.reviewButton = this.addReviewButton(left + label.width + 18, y);
      }
      this.add
        .text(right, y, value, { fontFamily: FONT_FAMILY, fontSize: '25px', color: PALETTE.snow })
        .setOrigin(1, 0.5);
    });
  }

  /** A small button beside the accuracy row; it joins the menu in buildMenu. */
  private addReviewButton(x: number, y: number): Phaser.GameObjects.Text {
    return this.add
      .text(x, y, 'What I missed', {
        fontFamily: FONT_FAMILY,
        fontSize: '19px',
        color: PALETTE.snow,
        backgroundColor: '#1b2a41',
        padding: { x: 12, y: 5 },
      })
      .setOrigin(0, 0.5)
      .setInteractive({ useHandCursor: true });
  }

  private openMisses(): void {
    const stats = this.stats;
    if (stats === null || this.panel !== null) return;
    this.panel = new MissesPanel(this, stats.wordSlips ?? [], stats.missedLetters, () => {
      this.panel = null;
    });
  }

  private powerUpSummary(stats: GameRunStats): string {
    const parts: string[] = [];
    if (stats.springsUsed > 0) parts.push(`${stats.springsUsed} spring`);
    if (stats.jetpacksUsed > 0) parts.push(`${stats.jetpacksUsed} jetpack`);
    if (parts.length === 0) return stats.bonusWordsHit > 0 ? `${stats.bonusWordsHit} hit` : 'none';
    return parts.join(', ');
  }

  /**
   * One line under the stats: the unlock if there is one, otherwise praise.
   *
   * They share the slot rather than stacking. The screen between the score and
   * the buttons is already full at seven stat rows, so a second line here has
   * nowhere to go - and an unlocked animal is better news than "nice typing",
   * so it is the one that gets said.
   */
  private showFooterMessage(stats: GameRunStats): void {
    const unlocked: unknown = this.registry.get('newUnlock');
    const unlockedId = typeof unlocked === 'string' ? unlocked : '';
    const message =
      unlockedId === ''
        ? encouragementFor(stats.accuracy, stats.reachedGoal)
        : `New animal unlocked: ${animalById(unlockedId)?.name ?? unlockedId}!`;
    if (message === null) return;

    // The fanfare belongs to the unlock, not to the praise line.
    if (unlockedId !== '') this.time.delayedCall(900, () => audioOf(this)?.play('unlock'));

    this.add
      .text(GAME_WIDTH / 2, IS_PORTRAIT ? GAME_HEIGHT - 480 : GAME_HEIGHT - 128, message, {
        fontFamily: FONT_FAMILY,
        fontSize: '24px',
        color: unlockedId === '' ? PALETTE.gold : PALETTE.teal,
        align: 'center',
        wordWrap: { width: Math.min(900, GAME_WIDTH - 60) },
      })
      .setOrigin(0.5);
  }

  /* ------------------------------------------------------------------ menu */

  private buildMenu(level: number): void {
    const nextLevel = Math.min(MAX_LEVEL, level + 1);
    this.items = [
      {
        label: level >= MAX_LEVEL ? 'Play level 100 again' : `Next: level ${nextLevel}`,
        action: () => this.scene.start('Game', { level: nextLevel }),
      },
      { label: 'Try this level again', action: () => this.scene.start('Game', { level }) },
      { label: 'Back to the map', action: () => this.scene.start('LevelMap') },
    ];
    // Offered after every level: the player's progress as a QR code or a
    // save picture, so it can follow them to another device.
    const profile = (this.registry.get('save') as SaveManager | undefined)?.activeProfile() ?? null;
    if (profile !== null) {
      this.items.push({
        label: 'Save progress',
        action: () => {
          this.panel = new SaveProgressPanel(this, profile, () => {
            this.panel = null;
          });
        },
      });
    }

    const y = GAME_HEIGHT - 62;
    const spacing = 360;
    this.items.forEach((item, index) => {
      // The first three sit along the bottom; Save progress sits top right.
      const extra = index >= 3 && !IS_PORTRAIT;
      // Tall stage: one full-width column of buttons up from the bottom.
      const stackY = GAME_HEIGHT - 70 - (this.items.length - 1 - index) * 92;
      const label = this.add
        .text(
          IS_PORTRAIT
            ? GAME_WIDTH / 2
            : extra
              ? GAME_WIDTH - 40
              : GAME_WIDTH / 2 + (index - 1) * spacing,
          IS_PORTRAIT ? stackY : extra ? 44 : y,
          item.label,
          {
            fontFamily: FONT_FAMILY,
            fontSize: '28px',
            color: PALETTE.snow,
            backgroundColor: '#1b2a41',
            padding: { x: 18, y: 10 },
          },
        )
        .setOrigin(extra ? 1 : 0.5, 0.5)
        .setInteractive({ useHandCursor: true });
      if (extra) label.setFontSize(24);
      if (IS_PORTRAIT) label.setFixedSize(GAME_WIDTH - 120, 0).setAlign('center');

      label.on('pointerover', () => this.select(index));
      label.on('pointerup', () => pressButton(label, item.action));
      this.labels.push(label);
    });

    // The review button sits up by the stats, but the arrow keys reach it too.
    const review = this.reviewButton;
    if (review !== null) {
      const index = this.items.length;
      const item = { label: review.text, action: () => this.openMisses() };
      review.on('pointerover', () => this.select(index));
      review.on('pointerup', () => pressButton(review, item.action));
      this.items.push(item);
      this.labels.push(review);
    }

    this.select(0);

    this.input.keyboard?.on('keydown', (event: KeyboardEvent) => {
      // An open overlay has the keyboard.
      if (this.panel !== null) return;
      if (event.key === 'ArrowLeft') {
        this.select((this.selected + this.items.length - 1) % this.items.length);
      } else if (event.key === 'ArrowRight') {
        this.select((this.selected + 1) % this.items.length);
      } else if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        const label = this.labels[this.selected];
        const item = this.items[this.selected];
        if (label !== undefined && item !== undefined) pressButton(label, item.action);
      } else if (event.key === 'Escape') {
        this.scene.start('LevelMap');
      }
    });
  }

  private select(index: number): void {
    this.selected = index;
    this.labels.forEach((label, i) => styleButton(label, i === index));
  }

  private drawBackdrop(): void {
    drawVerticalGradient(this, [
      [0x16233a, 0],
      [0x2b1b45, 0.45],
      [0x6b2d5c, 1],
    ]);
  }
}

function isRunStats(value: Partial<GameRunStats>): value is GameRunStats {
  return typeof value.level === 'number' && typeof value.platformCount === 'number';
}
